import React, { ChangeEvent, useEffect, useState } from "react";
import {
  ImageUploadUtil,
  ProductService,
  RecipeService,
} from "../../../APIs/index";

import { DataRecipe, Recipe } from "../../../@types/productInfo";

import RecipeTable from "./RecipesTable/RecipeTable";
import { useNotification } from "../../Toast/Notification";
import HeaderTable from "../HeaderTable/HeaderTable";
import RecipeModal from "../../Modal/RecipeModal/RecipeModal";
import { Modal } from "antd";
import useSearchItem from "../../../hooks/use-search-item";
import { usePagination } from "../../../hooks/use-pagination";

const Recipes: React.FC = () => {
  const [name, setName] = useState<string>("");
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRec, setFilteredRec] = useState<Recipe[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [description, setDescription] = useState<string>("");
  const [image, setImage] = useState<string | null>("");
  const [methods, setMethods] = useState<any[]>([]);
  const [newMethod, setNewMethod] = useState<string>("");
  const [ingredients, setIngredients] = useState<any[]>([]);
  const [newIngredient, setNewIngredient] = useState<string>("");
  const [meta_title, setMetaTitle] = useState<string>("");
  const [meta_description, setMetaDescription] = useState<string>("");
  const [selectedRec, setSelectedRec] = useState<any | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [productId, setProductId] = useState<any>();
  const [recipelink, setRecipeLink] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [isUpdating, setIsUpdating] = useState(false);

  const { notify } = useNotification();

  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 5;

  const handleButtonClick = () => {
    setIsModalVisible(true);
    setIsUpdating(false);
  };

  const resetForm = () => {
    setName("");
    setIngredients([]);
    setMethods([]);
    setNewMethod("");
    setNewIngredient("");
    setImage("");
    setDescription("");
    setMetaDescription("");
    setMetaTitle("");
    setRecipeLink("");
    setIngredients([]);
    setMethods([]);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    resetForm();
  };

  const handleFormSubmit = async () => {
    setIsLoading(true);
    const recipeData: Recipe = {
      name: name,
      ingredients: ingredients,
      methods: methods,
      description: description,
      image: image,
      productId,
      newMethod: "",
      newIngredient: "",
      meta_description: meta_description,
      meta_title: meta_title,
      recipelink: recipelink,
    };

    try {
      const { data } = await RecipeService.createRecipe(recipeData);

      if (data) {
        resetForm();
        fetchRecipeData();

        setSelectedRec(null);
        setIsModalVisible(false);
        handleModalCancel();
        notify("Recipe created successfully", "success");
      } else {
        console.error("Error creating recipe:");
        notify("Error creating recipe:", "error");
      }
    } catch (error) {
      console.error("Error creating recipe:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRecipe = async (recipeId: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this item?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        if (recipeId) {
          try {
            const { data } = await RecipeService.deleteRecipe(recipeId);

            if (data) {
              setRecipes((prev) =>
                prev.filter((recipe) => recipe.id !== recipeId)
              );

              notify("Recipe deleted successfully", "success");

              fetchRecipeData();

              setIsModalVisible(false);
              setSelectedRec(null);
            } else {
              notify("Error deleting recipe:", "error");
              setIsModalVisible(false);
            }
          } catch (error) {
            setIsModalVisible(false);
          } finally {
          }
        }
      },
    });
  };

  const handleEditRecipe = (rec: Recipe) => {
    if (rec) {
      setSelectedRec(rec);
      setName(rec.name || "");
      setIngredients(rec.ingredients || []);
      setMethods(rec.methods || []);
      setImage(rec.image || "");
      setNewMethod("");
      setDescription(rec.description || "");
      setMetaTitle(rec.meta_title || "");
      setMetaDescription(rec.meta_description || "");
      setRecipeLink(rec.recipelink || "");

      setProductId(rec.productId);
      setIsModalVisible(true);
      setIsUpdating(true);
    }
  };

  const handleSaveEdit = async (formData: DataRecipe) => {
    setIsLoading(true);

    try {
      if (selectedRec && selectedRec.id) {
        const updatedRecData = {
          name,
          ingredients,
          methods,
          description,
          image,
          productId: parseInt(productId),
          newMethod: "",
          newIngredient: "",
          meta_title,
          meta_description,
          recipelink,
        };

        notify("Recipe Updated successfully", "success");
        const { data, error } = await RecipeService.editRecipe(
          selectedRec.id,
          updatedRecData
        );

        if (data) {
          resetForm();
          fetchRecipeData();

          setIsModalVisible(false);
        } else {
          console.error("Error updating recipe:", error);
          setIsModalVisible(false);
        }
      } else {
        console.error("Selected recipe is undefined");
        notify("Error updating recipe:", "error");
        setIsModalVisible(false);
      }
    } catch (error) {
      console.error("Error uploading image or updating recipe:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRecipeData = async () => {
    try {
      const { data } = await RecipeService.getAllRecipes();

      if (data) {
        setRecipes(data);
        setFilteredRec(data);
      } else {
        console.error("Error fetching recipes:");
      }
    } catch (error) {
      console.error("Error fetching recipes:");
    }
  };

  useEffect(() => {
    fetchRecipeData();
  }, []);

  const fetchProductData = async () => {
    try {
      const { data, error } = await ProductService.getAllProducts();

      if (data) {
        setProducts(data);
      } else {
        console.error("Error fetching products:", error);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  useEffect(() => {
    fetchProductData();
  }, []);

  const handlePageChange = (pageNumber: any) => {
    setCurrentPage(pageNumber);
  };

  const filterProductsFromSearch = useSearchItem({
    items: recipes,
    searchTerm,
    selectedRole,
    searchFields: [
      "name",
      "description",
      "ingredients",
      "methods",
      "recipelink",
    ],
  });

  useEffect(() => {
    if (searchTerm.length > 0) {
    } else {
      setFilteredRec(recipes);
    }
  }, [recipes, filterProductsFromSearch, searchTerm]);

  const totalPages = Math.ceil(filteredRec.length / productsPerPage);
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredRec.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const pageNumbers = usePagination(totalPages);

  const handleSearchTermChange = (e: any) => {
    setSearchTerm(e.target.value);
  };

  const handleRoleChange = (event: any) => {
    setSelectedRole(event.target.value);
  };

  const handleFileChangeImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const uploadedImage = await ImageUploadUtil.uploadImage(file);
        if (uploadedImage.data) {
          setImage(uploadedImage.data);
        } else {
          console.error("Error uploading image:", uploadedImage.error);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }
  };

  return (
    <div className="p-6">
      <p
        className="mb-[15px] box-border font-semibold 
            text-[rgb(0,34,56)] block text-[35px] text-left h-auto
            leading-[38.4px]">
        Recipes
      </p>

      <div className="bg-white p-4 rounded-md w-5/4 mx-auto">
        <HeaderTable
          subtitle="Manage Recipes"
          buttonText="Add New Recipe"
          inputPlaceholder="Search Recipe"
          // selectPlaceholder="Filter By Product Type"
          loading={false}
          searchTerm={searchTerm}
          // selectedValue={selectedRole}
          onButtonClick={handleButtonClick}
          onSearchChange={handleSearchTermChange}
          onSelectChange={handleRoleChange}
          selectOptions={[
            { value: "flavour", label: "Flavours" },
            { value: "cylinders", label: "Cylinder" },
          ]}
        />
        <RecipeTable
          filteredRecipes={filteredRec}
          products={products}
          loading={isLoading}
          onEditOrder={handleEditRecipe}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          pageNumbers={pageNumbers}
          currentProducts={currentProducts}
          handleDeleteAcc={handleDeleteRecipe}
        />

        <RecipeModal
          visible={isModalVisible}
          onClose={handleModalCancel}
          isEditModal={isModalVisible}
          handleSaveEdit={handleSaveEdit}
          name={name}
          ingredients={ingredients}
          methods={methods}
          newMethod={newMethod}
          newIngredient={newIngredient}
          setName={setName}
          setIngredients={setIngredients}
          setMethods={setMethods}
          setNewMethod={setNewMethod}
          setNewIngredient={setNewIngredient}
          setImage={setImage}
          image={image}
          description={description}
          setDescription={setDescription}
          meta_description={meta_description}
          setMetaDescription={setMetaDescription}
          meta_title={meta_title}
          setMetaTitle={setMetaTitle}
          recipelink={recipelink}
          setRecipeLink={setRecipeLink}
          isProcessing={isLoading}
          loading={isLoading}
          isModalVisible={isModalVisible}
          handleFormSubmit={handleFormSubmit}
          handleFileChangeImage={handleFileChangeImage}
          handleModalCancel={handleModalCancel}
          productId={productId}
          setProductId={setProductId}
          products={products}
          isUpdating={isUpdating}
        />
      </div>
    </div>
  );
};

export default Recipes;
